import React, { useState, useEffect } from "react";
import Select from "react-select";
import {
  AiOutlineUserAdd,
  AiOutlineEdit,
  AiOutlineDelete,
  AiOutlineSearch,
  AiOutlineEye,
  AiOutlineFileExcel,
} from "react-icons/ai";
import {
  Row,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
} from "reactstrap";
import "../../../styles/dashboard/SuperAdmin/UserManagement.css";
import "../../../styles/dashboard/OrgUser/CreditCheck.css";
import {
  getOrganizations,
  getAdminOrganizations,
  getSubAdminOrganizations,
  fetchOrgUsers,
  createOrgUser,
  updateOrgUser,
  deleteOrgUser,
  toggleUserStatus,
} from "../../services/api";
import * as XLSX from "xlsx";

const generateEmployeeId = () => {
  const timestamp = Date.now().toString().slice(-6);
  return `EMP-${timestamp}`;
};

export default function UserManagement({ role = "superadmin" }) {
  // Role-based permissions for SuperAdmin/Admin/SubAdmin
  const canAdd = role === "superadmin" || role === "admin" || role === "subadmin";
  const canEdit = role === "superadmin" || role === "admin" || role === "subadmin";
  const canDelete = role === "superadmin"  // parity request
  const canViewDetails = true; // All roles can view
  const canToggleStatus = role === "superadmin" || role === "admin" || role === "subadmin";
  const canExport = role === "superadmin" || role === "admin" || role === "subadmin";
  const canManageCredits = role === "superadmin";

  // ────── STATE ──────
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [orgLoading, setOrgLoading] = useState(true);
  const usersPerPage = 10;

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState(""); // "add" | "edit" | "delete" | "view"
  const [selectedUser, setSelectedUser] = useState(null);

  // Toast
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    mobileNumber: "",
    username: "",
    password: "",
    organizationId: "",
    role: "CEO",
    address: "",
    aadhaarNumber: "",
    panNumber: "",
    employeeIdNumber: "",
    status: "Active",
    branch: "",
  });

  // ────── FETCH ORGANIZATIONS ──────
  useEffect(() => {
    setOrgLoading(true);
    const fetchOrgsAPI = role === "admin"
      ? getAdminOrganizations
      : role === "subadmin"
        ? getSubAdminOrganizations
        : getOrganizations;
    fetchOrgsAPI()
      .then((res) => {
        if (res.success) {
          const orgs = Array.isArray(res.data) ? res.data : (res.data?.data || []);
          console.log('All organizations:', orgs);
          if (orgs.length > 0) {
            console.log('Sample organization fields:', Object.keys(orgs[0]));
            console.log('Sample agreement object:', orgs[0].agreement);
          }
          // Show only Active orgs with Approved agreement; backend already scopes to assigned orgs for subadmin
          const activeOrgs = orgs.filter(org => {
            const status = org.status?.toLowerCase();
            const agreementStatus = org.agreement?.status?.toLowerCase();
            const currentDocStatus = org.agreement?.currentDocument?.status?.toLowerCase();
            console.log(`Org: ${org.organizationName}, Status: ${status}, Agreement: ${agreementStatus}, CurrentDoc: ${currentDocStatus}`);
            const isActive = status === 'active';
            const isAgreementApproved = agreementStatus === 'approved' || currentDocStatus === 'approved';
            return isActive && isAgreementApproved;
          });
          console.log('Filtered active organizations:', activeOrgs);
          setOrganizations(activeOrgs);
        } else {
          showToast(res.error || 'Failed to load organizations', 'danger');
        }
      })
      .catch(() => showToast("Failed to load organizations", "danger"))
      .finally(() => setOrgLoading(false));
  }, [role]);

  // ────── FETCH USERS ──────
  useEffect(() => {
    console.log('🚀 Starting fetchOrgUsers with role:', role);
    setLoading(true);
    fetchOrgUsers(role)
      .then((res) => {
        console.log('📦 Users API response:', res);
        if (res.success) {
          const userData = Array.isArray(res.data) ? res.data : (res.data?.data || []);
          console.log('👥 Processed user data:', userData);
          setUsers(userData);
        } else {
          console.error('❌ Users API failed:', res.error);
          showToast(res.error || 'Failed to load users', 'danger');
        }
      })
      .catch((error) => {
        console.error('❌ Users fetch error:', error);
        showToast("Failed to load users", "danger");
      })
      .finally(() => setLoading(false));
  }, [role]);

  // ────── SEARCH & FILTER ──────
  useEffect(() => {
    let filtered = users.filter(
      (u) =>
        u.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.employeeIdNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.username?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Filter by status
    if (filterStatus !== "all") {
      filtered = filtered.filter((u) => u.status === filterStatus);
    }

    // Filter by date range
    if (dateFrom) {
      filtered = filtered.filter((u) => {
        const userDate = new Date(u.createdAt);
        return userDate >= new Date(dateFrom);
      });
    }
    if (dateTo) {
      filtered = filtered.filter((u) => {
        const userDate = new Date(u.createdAt);
        return userDate <= new Date(dateTo);
      });
    }

    setFilteredUsers(filtered);
    setCurrentPage(1);
  }, [searchTerm, filterStatus, dateFrom, dateTo, users]);

  // ────── PAGINATION ──────
  const indexOfLast = currentPage * usersPerPage;
  const indexOfFirst = indexOfLast - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  // ────── TOAST ──────
  const showToast = (message, type = "info") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 3000);
  };

  // ────── VALIDATION ──────
  const validateField = (name, value) => {
    let error = "";
    const val = (value || "").toString().trim();

    switch (name) {
      case "fullName":
        if (!val) error = "Full Name is required";
        else if (val.length < 2) error = "Name must be at least 2 characters";
        break;
      case "username":
        if (!val) error = "Username is required";
        else if (!/^[a-zA-Z0-9._-]{3,}$/.test(val))
          error = "Min 3 chars, only letters, numbers, . _ -";
        break;
      case "email":
        if (!val) error = "Email is required";
        else if (!/^\S+@\S+\.\S+$/.test(val)) error = "Invalid email format";
        break;
      case "mobileNumber":
        if (val && !/^\d{10}$/.test(val.replace(/[-\s]/g, "")))
          error = "Mobile must be 10 digits";
        break;
      case "aadhaarNumber":
        if (val && !/^\d{12}$/.test(val)) error = "Aadhaar must be 12 digits";
        break;
      case "panNumber":
        if (val && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(val))
          error = "Invalid PAN (e.g. ABCDE1234F)";
        break;
      case "password":
        if (modalMode === "edit" && val && val.length < 6) error = "Password must be 6+ characters";
        break;
      case "organizationId":
        if (!val) error = "Organization is required";
        break;
      default:
        break;
    }
    return error;
  };

  // ────── FORM INPUT CHANGE ──────
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const normalizedValue = name === "panNumber" ? value.toUpperCase() : value;
    const updatedForm = { ...formData, [name]: normalizedValue };
    setFormData(updatedForm);

    const fieldError = validateField(name, normalizedValue);
    setErrors((prev) => {
      const newErr = { ...prev };
      if (fieldError) newErr[name] = fieldError;
      else delete newErr[name];
      return newErr;
    });
  };

  const validateForm = () => {
    const newErrors = {};
    ["fullName", "email", "username", "organizationId"].forEach((f) => {
      const err = validateField(f, formData[f]);
      if (err) newErrors[f] = err;
    });

    ["mobileNumber", "aadhaarNumber", "panNumber"].forEach((f) => {
      const err = validateField(f, formData[f]);
      if (err) newErrors[f] = err;
    });

    if (modalMode === "edit" && role !== "subadmin") {
      const passwordError = validateField("password", formData.password);
      if (passwordError) newErrors.password = passwordError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ────── MODAL OPEN/CLOSE ──────
  const openModal = (mode, user = null) => {
    setModalMode(mode);
    setSelectedUser(user);
    setErrors({});

    if (mode === "add") {
      const autoEmpId = generateEmployeeId();
      setFormData({
        fullName: "",
        email: "",
        mobileNumber: "",
        username: "",
        password: "",
        organizationId: "",
        role: "CEO",
        address: "",
        aadhaarNumber: "",
        panNumber: "",
        employeeIdNumber: autoEmpId,
        status: "Active",
      });
    } else if (mode === "edit" && user) {
      setFormData({
        fullName: user.fullName || "",
        email: user.email || "",
        mobileNumber: user.mobileNumber || "",
        username: user.username || "",
        password: "",
        organizationId: user.organizationId || "",
        role: user.role || "Employee",
        address: user.address || "",
        aadhaarNumber: user.aadhaarNumber || "",
        panNumber: user.panNumber || "",
        employeeIdNumber: user.employeeIdNumber || "",
        status: user.status || "Active",
        branch: user.branch || "",
      });
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalMode("");
    setSelectedUser(null);
    setErrors({});
  };

  // ────── SUBMIT (ADD/EDIT) ──────
  const handleSubmit = async () => {
    if (!validateForm()) {
      showToast("Please fix validation errors", "danger");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        fullName: formData.fullName?.trim(),
        email: formData.email?.trim(),
        mobileNumber: formData.mobileNumber || null,
        username: formData.username?.trim(),
        organizationId: formData.organizationId,
        role: formData.role,
        address: formData.address || null,
        aadhaarNumber: formData.aadhaarNumber || null,
        panNumber: formData.panNumber || null,
        employeeIdNumber: formData.employeeIdNumber || null,
        branch: formData.branch || "",
      };

      if (modalMode === "edit" && role !== "subadmin" && formData.password?.trim()) {
        payload.password = formData.password.trim();
      }

      if (modalMode === "add") {
        const result = await createOrgUser(payload, role);
        if (result.success) {
          showToast("User added successfully!", "success");
        } else {
          showToast(result.error || "Failed to add user", "danger");
        }
      } else if (modalMode === "edit") {
        const result = await updateOrgUser(selectedUser.id, payload, role);
        if (result.success) {
          showToast("User updated successfully!", "success");
        } else {
          showToast(result.error || "Update failed", "danger");
        }
      }

      // Refresh list
      const res = await fetchOrgUsers(role);
      if (res.success) {
        const newUsers = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        setUsers(newUsers);
      }
      closeModal();
    } catch (err) {
      const msg = err.response?.data?.message || "Operation failed";
      showToast(msg, "danger");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ────── DELETE ──────
  const handleDelete = async () => {
    try {
      const result = await deleteOrgUser(selectedUser.id, role);
      if (result.success) {
        showToast("User deleted successfully!", "success");
        setUsers((prev) => prev.filter((u) => u.id !== selectedUser.id));
      } else {
        showToast(result.error || "Delete failed", "danger");
      }
      closeModal();
    } catch (err) {
      showToast("Delete failed", "danger");
    }
  };

  // ────── STATUS CHANGE ──────
  const handleStatusChange = async (id, newStatus) => {
    try {
      const result = await toggleUserStatus(id, newStatus === "Active", role);
      if (result.success) {
        setUsers((prev) =>
          prev.map((u) => (u.id === id ? { ...u, status: newStatus, isActive: newStatus === "Active" } : u))
        );
        showToast("Status updated", "success");
      } else {
        showToast(result.error || "Failed to update status", "danger");
      }
    } catch (err) {
      showToast("Status update failed", "danger");
    }
  };

  // ────── EXPORT TO EXCEL ──────
  const handleExportToExcel = () => {
    const exportData = currentUsers.map((user, idx) => ({
      "Sr No.": indexOfFirst + idx + 1,
      "Full Name": user.fullName,
      "Email": user.email,
      "Username": user.username,
      "Mobile": user.mobileNumber || "-",
      "Organization": organizations.find((o) => o.id === user.organizationId)?.organizationName || "-",
      "Role": user.role,
      "Created Date": user.createdAt ? new Date(user.createdAt).toLocaleString() : "-",
      "Status": user.status,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Users");
    
    const fileName = `User_Management_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    
    showToast("Data exported successfully!", "success");
  };

  // ────── RENDER ──────
  return (
    <div className="um-container">
      {/* Toast */}
      {toast.show && (
        <div className={`um-toast um-toast-${toast.type}`}>
          <span>{toast.message}</span>
          <div className="um-toast-progress" />
        </div>
      )}

      <div className="um-header">
        <div>
          <h2>User Management</h2>
          <p className="text-muted">Manage organization user accounts</p>
        </div>
      </div>

      <div className="um-controls">
        <div className="um-search-bar">
          <AiOutlineSearch className="um-search-icon" />
          <input
            type="text"
            placeholder="Search by name, email, username..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="um-search-input"
          />
        </div>
      </div>

      <div className="um-filter-row">
        <div className="um-filter-group">
          <div className="um-date-filter">
            <label className="um-filter-label">Date Range:</label>
            <div className="um-date-inputs">
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="um-date-input"
                placeholder="From Date"
              />
              <span className="um-date-separator">to</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="um-date-input"
                placeholder="To Date"
              />
            </div>
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="um-filter-select"
          >
            <option value="all">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>

        <div className="um-action-buttons">
          <button className="um-export-btn" onClick={handleExportToExcel}>
            <AiOutlineFileExcel /> Export
          </button>
          <button className="um-add-btn" onClick={() => openModal("add")}>
            <AiOutlineUserAdd /> Add User
          </button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner color="primary" />
        </div>
      ) : (
        <>
          <div className="um-table-wrapper">
            <table className="um-table">
              <thead>
                <tr>
                  <th>Sr No.</th>
                  <th>Name</th>
                  {role !== "subadmin" && <th>Email</th>}
                  {role !== "subadmin" && <th>Username</th>}
                  {role !== "subadmin" && <th>Mobile</th>}
                  <th>Organization</th>
                  <th>Role</th>
                  <th>Created Date</th>
                  <th>Status</th>
                  {role !== "subadmin" && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {currentUsers.length === 0 ? (
                  <tr>
                    <td colSpan={role === "subadmin" ? "6" : "10"} className="um-no-data">
                      {users.length === 0 ? "Loading..." : "No users found"}
                    </td>
                  </tr>
                ) : (
                  currentUsers.map((user, idx) => {
                    const org = organizations.find((o) => o.id === user.organizationId);
                    return (
                      <tr key={user.id}>
                        <td>{indexOfFirst + idx + 1}</td>
                        <td className="um-name">{user.fullName}</td>
                        {role !== "subadmin" && <td className="um-email">{user.email}</td>}
                        {role !== "subadmin" && <td>{user.username}</td>}
                        {role !== "subadmin" && <td>{user.mobileNumber || "-"}</td>}
                        <td>{org?.organizationName || "-"}</td>
                        <td>
                          <span className={`um-role-badge ${user.role?.toLowerCase()}`}>
                            {user.role}
                          </span>
                        </td>
                        <td>{user.createdAt ? new Date(user.createdAt).toLocaleString() : "-"}</td>
                        <td>
                          <select
                            value={user.status || "Active"}
                            onChange={(e) => handleStatusChange(user.id, e.target.value)}
                            className={`um-status-select ${(user.status || "active").toLowerCase()}`}
                            disabled={role === "subadmin"}
                          >
                            <option value="Active">Active</option>
                            <option value="Inactive">Inactive</option>
                          </select>
                        </td>
                        {role !== "subadmin" && (
                          <td>
                            <div className="um-actions">
                              <button
                                className="um-action-btn view"
                                onClick={() => openModal("view", user)}
                                title="View Details"
                              >
                                <AiOutlineEye size={18} />
                              </button>
                              <button
                                className="um-action-btn edit"
                                onClick={() => openModal("edit", user)}
                                title="Edit"
                              >
                                <AiOutlineEdit size={18} />
                              </button>
                               {canDelete && (<button
                                className="um-action-btn delete"
                                onClick={() => openModal("delete", user)}
                                title="Delete"
                              >
                                <AiOutlineDelete size={18} />
                              </button>)}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="um-pagination-wrapper">
              <div className="um-pagination-info">
                Showing {indexOfFirst + 1} to {Math.min(indexOfLast, filteredUsers.length)} of {filteredUsers.length} entries
              </div>
              <div className="um-pagination">
                <button 
                  className="um-page-btn" 
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(p => p - 1)}
                >
                  Previous
                </button>
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    className={`um-page-btn ${i + 1 === currentPage ? 'active' : ''}`}
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}
                <button 
                  className="um-page-btn" 
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(p => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ────── MODAL ────── */}
      {modalOpen && (
        <div className="credit-modal-overlay" >
          <div className="credit-modal-content wide" onClick={e => e.stopPropagation()}>
            <div className="credit-modal-header">
              <h2>
                {modalMode === "add"
                  ? "Add New User"
                  : modalMode === "edit"
                    ? "Edit User"
                    : modalMode === "view"
                      ? "User Details"
                      : "Delete User"}
              </h2>
              <button className="credit-modal-close" onClick={closeModal}>×</button>
            </div>

            <div className="credit-modal-body">
          {/* VIEW MODE */}
          {modalMode === "view" && selectedUser && (
            <>
              {/* Basic Information */}
              <h6 className="um-category-title">Basic Information</h6>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label className="um-form-label">Full Name</Label>
                    <Input value={selectedUser.fullName || "-"} readOnly disabled />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="um-form-label">Username</Label>
                    <Input value={selectedUser.username || "-"} readOnly disabled />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="um-form-label">Organization</Label>
                    <Input 
                      value={organizations.find((o) => o.id === selectedUser.organizationId)?.organizationName || "-"} 
                      readOnly 
                      disabled 
                    />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="um-form-label">Employee Type</Label>
                    <Input value={selectedUser.role || "-"} readOnly disabled />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="um-form-label">Branch Name</Label>
                    <Input value={selectedUser.branch || "-"} readOnly disabled />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="um-form-label">Address</Label>
                    <Input value={selectedUser.address || "-"} readOnly disabled />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="um-form-label">Aadhaar Number</Label>
                    <Input value={selectedUser.aadhaarNumber || "-"} readOnly disabled />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="um-form-label">PAN Number</Label>
                    <Input value={selectedUser.panNumber || "-"} readOnly disabled />
                  </FormGroup>
                </Col>

                {role !== "subadmin" && (
                  <Col md="6">
                    <FormGroup>
                      <Label className="um-form-label">Password</Label>
                      <Input value="********" readOnly disabled />
                    </FormGroup>
                  </Col>
                )}
              </Row>

              {/* Contact Information */}
              <h6 className="um-category-title">Contact Information</h6>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label className="um-form-label">Email</Label>
                    <Input value={selectedUser.email || "-"} readOnly disabled />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="um-form-label">Mobile Number</Label>
                    <Input value={selectedUser.mobileNumber || "-"} readOnly disabled />
                  </FormGroup>
                </Col>
              </Row>

              {/* System Information */}
              <h6 className="um-category-title">System Information</h6>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label className="um-form-label">Status</Label>
                    <Input value={selectedUser.status || "Active"} readOnly disabled />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="um-form-label">Created At</Label>
                    <Input 
                      value={selectedUser.createdAt ? new Date(selectedUser.createdAt).toLocaleString() : "-"} 
                      readOnly 
                      disabled 
                    />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="um-form-label">Updated At</Label>
                    <Input 
                      value={selectedUser.updatedAt ? new Date(selectedUser.updatedAt).toLocaleString() : "-"} 
                      readOnly 
                      disabled 
                    />
                  </FormGroup>
                </Col>
              </Row>
            </>
          )}

          {/* DELETE MODE */}
          {modalMode === "delete" && selectedUser && (
            <p>
              Are you sure you want to delete <strong>{selectedUser.fullName}</strong>?
            </p>
          )}

          {/* ADD/EDIT MODE */}
          {modalMode !== "view" && modalMode !== "delete" && (
            <>
              <Row>
                {/* Full Name */}
                <Col md="6">
                  <FormGroup>
                    <Label className="um-form-label">Full Name <span className="text-danger">*</span></Label>
                    <Input
                      type="text"
                      name="fullName"
                      placeholder="Full Name *"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      invalid={!!errors.fullName}
                      className="mb-3"
                    />
                    {errors.fullName && <small className="text-danger">{errors.fullName}</small>}
                  </FormGroup>
                </Col>

                {/* Username */}
                <Col md="6">
                  <FormGroup>
                    <Label className="um-form-label">Username <span className="text-danger">*</span></Label>
                    <Input
                      type="text"
                      name="username"
                      placeholder="Username *"
                      value={formData.username}
                      onChange={handleInputChange}
                      invalid={!!errors.username}
                      className="mb-3"
                    />
                    {errors.username && <small className="text-danger">{errors.username}</small>}
                  </FormGroup>
                </Col>

                {/* Email */}
                <Col md="6">
                  <FormGroup>
                    <Label className="um-form-label">Email <span className="text-danger">*</span></Label>
                    <Input
                      type="email"
                      name="email"
                      placeholder="Email *"
                      value={formData.email}
                      onChange={handleInputChange}
                      invalid={!!errors.email}
                      className="mb-3"
                    />
                    {errors.email && <small className="text-danger">{errors.email}</small>}
                  </FormGroup>
                </Col>

                {/* Mobile Number */}
                <Col md="6">
                  <FormGroup>
                    <Label className="um-form-label">Mobile Number</Label>
                    <Input
                      type="text"
                      name="mobileNumber"
                      placeholder="Mobile (10 digits)"
                      value={formData.mobileNumber}
                      onChange={handleInputChange}
                      invalid={!!errors.mobileNumber}
                      className="mb-3"
                    />
                    {errors.mobileNumber && <small className="text-danger">{errors.mobileNumber}</small>}
                  </FormGroup>
                </Col>

                {/* Organization */}
                <Col md="6">
                  <FormGroup>
                    <Label className="um-form-label">
                      Organization <span className="text-danger">*</span>
                    </Label>

                    <Select
                      autoComplete="new-password"
                      isSearchable
                      isDisabled={orgLoading}
                      placeholder="Select Organization"
                      value={
                        organizations
                          .map(org => ({ value: org.id, label: org.organizationName }))
                          .find(opt => opt.value === formData.organizationId) || null
                      }
                      options={organizations.map(org => ({
                        value: org.id,
                        label: org.organizationName
                      }))}
                      onChange={(selected) =>
                        handleInputChange({
                          target: {
                            name: "organizationId",
                            value: selected ? selected.value : ""
                          }
                        })
                      }
                      classNamePrefix="react-select"
                    />

                    {errors.organizationId && (
                      <small className="text-danger">{errors.organizationId}</small>
                    )}
                  </FormGroup>
                </Col>

                {/* Employee Type */}
                <Col md="6">
                  <FormGroup>
                    <Label className="um-form-label">Employee Type</Label>
                    <Input
                      type="select"
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      className="mb-3"
                    >
                      <option value="CEO">CEO</option>
                      <option value="MD">MD</option>
                      <option value="HR">HR</option>
                      <option value="Senior Manager">Senior Manager</option>
                      <option value="Manager">Manager</option>
                      <option value="Recovery Office">Recovery Office</option>
                      <option value="Clerk">Clerk</option>
                    </Input>
                  </FormGroup>
                </Col>

                {/* Password is auto-generated in Add mode */}
                {modalMode === "add" && (
                  <Col md="6">
                    <FormGroup>
                      <Label className="um-form-label">Password</Label>
                      <Input
                        type="text"
                        value="Auto-generated and sent by system"
                        disabled
                        className="mb-1"
                      />
                      <small className="text-muted">A temporary password will be generated automatically when this user is created.</small>
                    </FormGroup>
                  </Col>
                )}

                {modalMode === "edit" && role !== "subadmin" && (
                  <Col md="6">
                    <FormGroup>
                      <Label className="um-form-label">Password</Label>
                      <Input
                        type="password"
                        name="password"
                        placeholder="Enter new password"
                        value={formData.password}
                        onChange={handleInputChange}
                        invalid={!!errors.password}
                        className="mb-1"
                      />
                      <small className="text-muted">Leave blank to keep the current password.</small>
                      {errors.password && <small className="text-danger d-block">{errors.password}</small>}
                    </FormGroup>
                  </Col>
                )}


                {/* Address */}
                <Col md="6">
                  <FormGroup>
                    <Label className="um-form-label">Address</Label>
                    <Input
                      name="address"
                      placeholder="Address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className="mb-3"
                    />
                  </FormGroup>
                </Col>

                {/* Branch Name */}
                <Col md="6">
                  <FormGroup>
                    <Label className="um-form-label">Branch Name</Label>
                    <Input
                      name="branch"
                      placeholder="Enter Branch Name"
                      value={formData.branch}
                      onChange={handleInputChange}
                      className="mb-3"
                    />
                  </FormGroup>
                </Col>

                {/* Aadhaar Number */}
                <Col md="6">
                  <FormGroup>
                    <Label className="um-form-label">Aadhaar Number</Label>
                    <Input
                      name="aadhaarNumber"
                      placeholder="Aadhaar (12 digits)"
                      value={formData.aadhaarNumber}
                      onChange={handleInputChange}
                      invalid={!!errors.aadhaarNumber}
                      maxLength="12"
                      className="mb-3"
                    />
                    {errors.aadhaarNumber && <small className="text-danger">{errors.aadhaarNumber}</small>}
                  </FormGroup>
                </Col>

                {/* PAN Number */}
                <Col md="6">
                  <FormGroup>
                    <Label className="um-form-label">PAN Number</Label>
                    <Input
                      name="panNumber"
                      placeholder="PAN (e.g. ABCDE1234F)"
                      value={formData.panNumber}
                      onChange={handleInputChange}
                      invalid={!!errors.panNumber}
                      maxLength="10"
                      className="mb-3 um-pan-input"
                    />
                    {errors.panNumber && <small className="text-danger">{errors.panNumber}</small>}
                  </FormGroup>
                </Col>
              </Row>
            </>
          )}
        </div>

        <div className="credit-modal-footer">
          {modalMode === "delete" ? (
            <>
              <button className="um-modal-btn um-btn-danger" onClick={handleDelete}>
                Delete
              </button>
              <button className="um-modal-btn um-btn-secondary" onClick={closeModal}>
                Cancel
              </button>
            </>
          ) : modalMode === "view" ? (
            <button className="um-modal-btn um-btn-secondary" onClick={closeModal}>
              Close
            </button>
          ) : (
            <>
              <button 
                className="um-modal-btn um-btn-primary" 
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? <Spinner size="sm" /> : (modalMode === "edit" ? "Update" : "Add")}
              </button>
              <button className="um-modal-btn um-btn-secondary" onClick={closeModal}>
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    </div>
      )}
    </div>
  );
}
